import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FolderIcon, ArrowLeftIcon, TrashIcon, LinkIcon, ShareIcon, ClipboardDocumentIcon, ClipboardDocumentCheckIcon, GlobeAltIcon, LockClosedIcon, Bars3Icon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import HelpTooltip from '../components/HelpTooltip';
import { bookmarkService } from '../services/bookmarkService';

const CollectionDetail = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bookmarkService.getCollection(collectionId);
      setCollection(data);
    } catch (err) {
      setError(err.message || 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  }, [collectionId]);

  useEffect(() => { load(); }, [load]);

  const shareUrl = collection?.shareToken
    ? `${window.location.origin}/shared/${collection.shareToken}`
    : null;

  const handleShare = async () => {
    setShareLoading(true);
    setShareError(null);
    try {
      const result = await bookmarkService.shareCollection(collectionId);
      setCollection(prev => ({ ...prev, shareToken: result.shareToken, isShared: true }));
    } catch (err) {
      setShareError(err.message || 'Failed to share collection');
    } finally {
      setShareLoading(false);
    }
  };

  const handleUnshare = async () => {
    if (!window.confirm('Stop sharing this collection? The share link will stop working.')) return;
    setShareLoading(true);
    setShareError(null);
    try {
      await bookmarkService.unshareCollection(collectionId);
      setCollection(prev => { const { shareToken: _, ...rest } = prev; return { ...rest, isShared: false }; });
    } catch (err) {
      setShareError(err.message || 'Failed to stop sharing');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRemove = async (bookmarkId) => {
    try {
      await bookmarkService.removeBookmarkFromCollection(collectionId, bookmarkId);
      setCollection(prev => ({
        ...prev,
        bookmarks: prev.bookmarks.filter(b => b.bookmarkId !== bookmarkId),
        bookmarkIds: prev.bookmarkIds.filter(id => id !== bookmarkId),
        bookmarkCount: (prev.bookmarkCount || 1) - 1,
      }));
    } catch (err) {
      alert(err.message || 'Failed to remove bookmark');
    }
  };

  const handleDeleteCollection = async () => {
    if (!window.confirm('Delete this collection? The bookmarks inside will not be deleted.')) return;
    try {
      await bookmarkService.deleteCollection(collectionId);
      navigate('/collections');
    } catch (err) {
      alert(err.message || 'Failed to delete collection');
    }
  };

  const startEdit = () => {
    setEditName(collection.name || '');
    setEditDescription(collection.description || '');
    setEditError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditError(null);
  };

  const saveEdit = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) { setEditError('Collection name cannot be empty.'); return; }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await bookmarkService.updateCollection(collectionId, {
        name: trimmedName,
        description: editDescription.trim(),
      });
      setCollection(prev => ({ ...prev, name: updated.name, description: updated.description }));
      setEditing(false);
    } catch (err) {
      setEditError(err.message || 'Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  // Drag-and-drop reorder
  const dragIndex = useRef(null);
  const handleDragStart = (index) => { dragIndex.current = index; };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    setCollection(prev => {
      const items = [...prev.bookmarks];
      const [moved] = items.splice(dragIndex.current, 1);
      items.splice(index, 0, moved);
      dragIndex.current = index;
      return { ...prev, bookmarks: items };
    });
  };
  const handleDrop = async () => {
    dragIndex.current = null;
    const newIds = collection.bookmarks.map(b => b.bookmarkId);
    try {
      await bookmarkService.updateCollection(collectionId, { bookmarkIds: newIds });
    } catch (err) {
      alert(err.message || 'Failed to save new order');
      load(); // revert
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/collections" className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Collections
        </Link>
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      </div>
    );
  }

  const bookmarks = collection?.bookmarks || [];
  const isShared = !!collection?.shareToken;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back */}
      <Link to="/collections" className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3">
        <ArrowLeftIcon className="h-4 w-4" /> Back to Collections
      </Link>

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <FolderIcon className="h-8 w-8 text-primary-500 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              {editing ? (
                <div className="space-y-2">
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    className="w-full text-xl font-bold text-gray-900 dark:text-gray-100 border border-primary-300 dark:border-primary-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Collection name"
                    maxLength={100}
                  />
                  <textarea
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    rows={2}
                    className="w-full text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Add a description (optional)"
                    maxLength={500}
                  />
                  {editError && <p className="text-xs text-red-600">{editError}</p>}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={saveEdit}
                      disabled={editSaving}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4" /> {editSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center gap-1 px-3 py-1.5 text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 group">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{collection?.name}</h1>
                      {isShared && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium flex-shrink-0">
                          <GlobeAltIcon className="h-3 w-3" /> Shared
                        </span>
                      )}
                    </div>
                    {collection?.description
                      ? <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{collection.description}</p>
                      : <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5 italic">No description — click edit to add one</p>
                    }
                  </div>
                  <button
                    onClick={startEdit}
                    className="flex-shrink-0 p-1.5 text-gray-300 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100 mt-0.5"
                    title="Edit name and description"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {!editing && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            <HelpTooltip id="collection-share" />
            <button
              onClick={isShared ? handleUnshare : handleShare}
              disabled={shareLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isShared
                  ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50'
                  : 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={isShared ? 'Stop sharing' : 'Share collection'}
            >
              {isShared
                ? <><GlobeAltIcon className="h-4 w-4" /> Shared</>
                : <><ShareIcon className="h-4 w-4" /> Share</>
              }
            </button>
            <button
              onClick={handleDeleteCollection}
              className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title="Delete collection"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
          )}
        </div>

        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2 sm:ml-11">
          {bookmarks.length === 1 ? '1 bookmark' : `${bookmarks.length} bookmarks`}
        </p>

        {/* Share panel */}
        {isShared && shareUrl && (
          <div className="mt-3 sm:ml-11 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3">
            <p className="text-xs text-green-700 dark:text-green-400 font-medium mb-2">Anyone with this link can view this collection</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 bg-white dark:bg-gray-700 border border-green-200 dark:border-green-700 rounded-lg px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 min-w-0 focus:outline-none"
                onFocus={e => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-700 border border-green-200 dark:border-green-700 rounded-lg text-sm text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors flex-shrink-0"
              >
                {copied
                  ? <><ClipboardDocumentCheckIcon className="h-4 w-4" /> Copied!</>
                  : <><ClipboardDocumentIcon className="h-4 w-4" /> Copy</>
                }
              </button>
            </div>
            <button
              onClick={handleUnshare}
              disabled={shareLoading}
              className="mt-2 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <LockClosedIcon className="h-3 w-3" /> Stop sharing
            </button>
          </div>
        )}

        {shareError && (
          <p className="mt-2 sm:ml-11 text-sm text-red-600 dark:text-red-400">{shareError}</p>
        )}
      </div>

      {/* Empty state */}
      {bookmarks.length === 0 && (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <FolderIcon className="h-16 w-16 mx-auto mb-4 opacity-40" />
          <p className="text-lg font-medium">This collection is empty</p>
          <p className="text-sm mt-1">
            Add bookmarks from{' '}
            <Link to="/bookmarks" className="text-primary-600 dark:text-primary-400 hover:underline">My Bookmarks</Link>{' '}
            using the "Add to Collection" action.
          </p>
        </div>
      )}

      {/* Bookmark list */}
      {bookmarks.length > 0 && (
        <ul className="space-y-3">
          {bookmarks.map((b, i) => (
            <BookmarkRow
              key={b.bookmarkId}
              bookmark={b}
              onRemove={handleRemove}
              onDragStart={() => handleDragStart(i)}
              onDragOver={e => handleDragOver(e, i)}
              onDrop={handleDrop}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const BookmarkRow = ({ bookmark, onRemove, onDragStart, onDragOver, onDrop }) => {
  const domain = (() => {
    try { return new URL(bookmark.url).hostname.replace(/^www\./, ''); }
    catch { return ''; }
  })();

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-4 flex items-start gap-4 hover:shadow-sm transition-shadow cursor-grab active:cursor-grabbing active:opacity-60"
    >
      <Bars3Icon className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
      <LinkIcon className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <a
          href={bookmark.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors line-clamp-1"
        >
          {bookmark.title || bookmark.url}
        </a>
        {domain && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{domain}</p>}
        {bookmark.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{bookmark.description}</p>
        )}
        {bookmark.tags && bookmark.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {bookmark.tags.slice(0, 5).map(t => (
              <span key={t} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">{t}</span>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => onRemove(bookmark.bookmarkId)}
        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
        title="Remove from collection"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </li>
  );
};

export default CollectionDetail;
